import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { supabase, syncTokenFromSession } from "@/lib/supabaseClient";
import { primeAudio } from "@/lib/pdaSounds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pared-down login for the PDA wrapper. Same Supabase auth path as the
 * full web app, just larger inputs and no marketing copy. Stores the
 * profile and token so the rest of /pda can read `localStorage.token`.
 */
export default function PdaLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(localStorage.getItem("pda_last_email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!email.trim() || !password) {
      toast.error("Enter email and password");
      return;
    }
    setLoading(true);
    primeAudio();
    try {
      try { await supabase.auth.signOut({ scope: "local" }); } catch {}
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        const m = (error.message || "").toLowerCase();
        if (m.includes("invalid") || m.includes("credentials")) {
          toast.error("Wrong email or password");
        } else {
          toast.error(error.message || "Login failed");
        }
        return;
      }
      syncTokenFromSession(data.session);
      const meResp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${data.session.access_token}` },
        validateStatus: () => true,
      });
      if (meResp.status < 200 || meResp.status >= 300) {
        toast.error("Couldn't load profile");
        return;
      }
      const profile = meResp.data;
      // Any warehouse-role account can log into the PDA: office staff
      // (admin/manager) AND drivers. Hotel users are still blocked because
      // the PDA shows nothing useful to them.
      const role = profile.role || "customer";
      if (role !== "admin" && role !== "manager" && role !== "driver") {
        toast.error("This account isn't a warehouse operator");
        try { await supabase.auth.signOut(); } catch {}
        return;
      }
      localStorage.setItem("pda_last_email", email.trim());
      onLogin?.(profile, data.session.access_token);
      navigate("/pda/menu", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-900 text-white flex items-center justify-center px-4" data-testid="pda-login">
      <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4">
        <div className="text-center mb-6">
          <div className="text-3xl font-display font-bold tracking-tight">DRRT · PDA</div>
          <div className="text-xs text-neutral-400 mt-1">Warehouse scanner — sign in to continue</div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="pda-login-email"
            autoComplete="username"
            className="w-full h-12 px-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-base focus:outline-none focus:border-amber-400"
            placeholder="you@drrt.com.au"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="pda-login-password"
            autoComplete="current-password"
            className="w-full h-12 px-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-base focus:outline-none focus:border-amber-400"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="pda-login-submit"
          className="w-full h-12 rounded-md bg-amber-400 text-neutral-900 font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign in <ArrowRight className="h-5 w-5" /></>}
        </button>
      </form>
    </div>
  );
}
